/**
 * @file 参考書ルートの「スケジュール」— 学習者 × 科目の設定と、そこから組んだ学習スケジュール。
 *
 * @what: 学習者を選び、受験の対象・目標の志望校・試験日 (期限)・起点の日と、科目ごとの設定 (受験科目か・
 *   初学者か学び直しか・いまの偏差値・周回数・週の時間) をフォームで保存する。保存した設定と
 *   参考書ルートから、全科目を1枚の時系列の表 (行 = 科目 × 列、列 = 週か月) に並べ、その下に科目
 *   ごとの区分の列 (並行 / 順番) と本の見積もりを出す。本には表紙を添え、タップで拡大できる。
 *   初学者の科目は始める日を入れず、試験日と区分の時期から「いつから始めるか」を出す。始める日の欄は
 *   学び直しの科目があるときだけ出す (代表の指摘、2026-09-05)。終わりの日の呼び名は、受験のルートでは
 *   「試験日」、目盛りの定義を持つルート (教養・技能・資格) では「期限」で通す (deadlineNoun)。
 * @why: スケジュールは保存せず毎回計算する (設計 §3.3)。週の時間は任意で、無ければ試験日から
 *   「終えるには週 X 分」を逆算する (代表の指摘、2026-09-05)。量の分からない本は「量が未登録」と出し、
 *   合計から外れていることを隠さない。
 * @failure: 設定の取得失敗と保存失敗は1行で知らせる (詳細は console のみ)。学習者が選ばれていない
 *   ときは案内だけを出す。期限も量の分かる本も無く期間が決まらないときは時系列の表を出さず、何を入れれば
 *   出るかの案内に替える。受験科目が無いときも案内だけにする。表紙は ISBN も Amazon の URL も無い本、
 *   書影の取得に失敗した本では頭文字だけの表示になる (MaterialCover の既定)。
 */

import { Fragment, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  STUDY_EXAM_MODE_LABELS,
  type StudyExamMode,
  type StudyExamPlanInput,
  type StudyExamSubjectSetting,
} from "../api/examPlans";
import { todayLocal } from "../api/dates";
import { useExamPlan } from "../hooks/useExamPlan";
import {
  DEFAULT_ROUNDS,
  buildSchedule,
  buildTimeline,
  formatMinutes,
  sliceTimeline,
  timelinePhases,
  type Lane,
  type ScheduledBook,
  type ScheduleTimeline,
  type SubjectSchedule,
  type TimelinePhase,
} from "../model/examSchedule";
import {
  deadlineNoun,
  scaleMarkLabel,
  scaleNoun,
  type MergedTarget,
} from "../model/textbookRouteGrid";
import { fetchLearnerBirth } from "../api/persons";
import {
  examDateFromBirth,
  formatMonthDay,
  gradeLabel,
  gradeLabelFromBirth,
} from "../model/schoolYear";
import { MaterialCover } from "./MaterialsPanel";
import { NO_SHELF_BOOKS, type ShelfBooks } from "../model/shelfBooks";
import { activitySeconds, touchedWithin, type ActivityByIsbn } from "../model/plannedRanges";
import { addDaysLocal } from "../api/dates";
import { weekStartOf } from "../model/studyDays";
import "./study.css";

interface SubjectForm {
  enabled: boolean;
  mode: StudyExamMode;
  currentDeviation: string;
  rounds: string;
  weeklyMinutes: string;
  /** 使わない区分 (tracks[].id)。 */
  excludedTracks: string[];
}

interface PlanForm {
  goalSchool: string;
  examDate: string;
  startDate: string;
  subjects: Record<string, SubjectForm>;
}

function emptySubject(): SubjectForm {
  return {
    enabled: false,
    mode: "beginner",
    currentDeviation: "",
    rounds: "",
    weeklyMinutes: "",
    excludedTracks: [],
  };
}

function formFromPlan(
  merged: MergedTarget,
  plan: {
    goal_school?: string;
    exam_date?: string;
    start_date?: string;
    subjects: Record<string, StudyExamSubjectSetting>;
  } | null,
): PlanForm {
  const subjects: Record<string, SubjectForm> = {};
  for (const s of merged.subjects) {
    const st = plan?.subjects[s.id];
    subjects[s.id] = st
      ? {
          enabled: st.enabled,
          mode: st.mode,
          currentDeviation: st.current_deviation ? String(st.current_deviation) : "",
          rounds: st.rounds ? String(st.rounds) : "",
          weeklyMinutes: st.weekly_minutes ? String(st.weekly_minutes) : "",
          excludedTracks: st.excluded_tracks ? [...st.excluded_tracks] : [],
        }
      : emptySubject();
  }
  return {
    goalSchool: plan?.goal_school ?? "",
    examDate: plan?.exam_date ?? "",
    startDate: plan?.start_date ?? "",
    subjects,
  };
}

function num(s: string): number | undefined {
  const v = Number(s);
  return s.trim() !== "" && Number.isFinite(v) && v > 0 ? v : undefined;
}

function toInput(merged: MergedTarget, form: PlanForm): StudyExamPlanInput {
  const school = merged.schools.find((sc) => sc.name === form.goalSchool);
  const subjects: Record<string, StudyExamSubjectSetting> = {};
  for (const [id, f] of Object.entries(form.subjects)) {
    if (
      !f.enabled &&
      f.mode === "beginner" &&
      !f.currentDeviation &&
      !f.rounds &&
      !f.weeklyMinutes &&
      f.excludedTracks.length === 0
    )
      continue;
    const st: StudyExamSubjectSetting = { enabled: f.enabled, mode: f.mode };
    const cd = num(f.currentDeviation);
    const r = num(f.rounds);
    const w = num(f.weeklyMinutes);
    if (cd !== undefined) st.current_deviation = cd;
    if (r !== undefined) st.rounds = Math.round(r);
    if (w !== undefined) st.weekly_minutes = Math.round(w);
    if (f.excludedTracks.length > 0) st.excluded_tracks = [...f.excludedTracks];
    subjects[id] = st;
  }
  const input: StudyExamPlanInput = { target: merged.target, subjects };
  if (school) {
    input.goal_school = school.name;
    input.goal_deviation = school.deviation;
  }
  if (form.examDate) input.exam_date = form.examDate;
  if (form.startDate) input.start_date = form.startDate;
  return input;
}

/**
 * 設定の読み書きを外から渡すための最小の形を定める。
 *
 * @why: 学びの計画の器では、一覧を持つ側 (LearningPlansPanel) が計画を読み書きする。この面が自分で
 *   読むと、一覧と面で2回読むことになり、保存のあとに一覧が古くなる。
 */
export interface ExamPlanStore {
  state:
    | { status: "loading" }
    | { status: "ready"; plan: (StudyExamPlanInput & { target: string }) | null }
    | { status: "error" };
  save: (input: StudyExamPlanInput) => Promise<boolean>;
  saving: boolean;
  saveError: string | null;
}

export function ExamPlanPanel({
  familyId,
  learnerPersonId,
  learnerLabel,
  merged,
  shelf = NO_SHELF_BOOKS,
  store,
  activity,
}: {
  familyId: string;
  learnerPersonId: string | null;
  learnerLabel: string;
  merged: MergedTarget;
  /** 本だなにある本の判定を渡す。表紙の左下に印を出すのに使う。 */
  shelf?: ShelfBooks;
  /** 設定の読み書きを外から渡す。無ければ前の器 (exam_plans) を自分で読む。 */
  store?: ExamPlanStore;
  /**
   * 本 (ISBN) ごとの、日付 → 実際に操作していた秒数である。時系列の表の本の下に触れた週の印を置き、科目の行に
   * 今週の実績を出す。渡さなければどちらも出さない。
   */
  activity?: ActivityByIsbn;
}) {
  const own = useExamPlan(
    familyId,
    learnerPersonId,
    store === undefined && learnerPersonId !== null,
  );
  const { state, save, saving, saveError } = store ?? own;
  // 終わりの日の呼び名 (受験は「試験日」、教養・資格のカリキュラムは「期限」)。
  const deadline = deadlineNoun(merged);
  const [form, setForm] = useState<PlanForm>(() => formFromPlan(merged, null));
  // 学習者の生年月日 (試験日を出すのに使う)。取れなければ null のまま手入力に任せる
  const [birthDate, setBirthDate] = useState<string | null>(null);
  // 読み込みが終わるまで「生年月日があれば自動で入る」の案内を出さない (一瞬出て消えるのを避ける)
  const [birthLoaded, setBirthLoaded] = useState(false);
  useEffect(() => {
    setBirthDate(null);
    setBirthLoaded(false);
    if (learnerPersonId === null) return;
    const controller = new AbortController();
    fetchLearnerBirth(learnerPersonId, controller.signal)
      .then((b) => {
        if (controller.signal.aborted) return;
        setBirthDate(b.birthDate);
        setBirthLoaded(true);
      })
      .catch((e: unknown) => {
        if (controller.signal.aborted) return;
        console.warn("[study] failed to load learner birth", e);
        setBirthLoaded(true);
      });
    return () => controller.abort();
  }, [learnerPersonId]);
  // 学年は学習者の生年月日から出す (試験の学年から逆算すると、おとなの学び直しにも「高3」が付く)
  const gradeOf = useMemo(
    () => (birthDate !== null ? (d: string) => gradeLabelFromBirth(d, birthDate) : null),
    [birthDate],
  );
  const derivedExamDate =
    birthDate !== null && merged.examGrade !== null && merged.examMonthDay !== null
      ? examDateFromBirth(birthDate, merged.examGrade, merged.examMonthDay)
      : null;
  const [dirty, setDirty] = useState(false);

  const saved =
    state.status === "ready" && state.plan && state.plan.target === merged.target
      ? state.plan
      : null;
  /*
   * 読み込んだ設定でフォームを埋める (対象が変わったときも作り直す)。
   *
   * ※なぜなら useEffect で埋めると画面に出したあとに値が入るので、値の無いフォームを1度出してから
   *   埋めることになり、生年月日を待つ理由 (空を見せてから伸びるのを避ける) と食い違うためである。
   *   描画の中で入れ替えると、最初に出る時点で値が入っている。
   *
   * 埋めた設定は参照ではなく中身で見分ける。※なぜなら一覧の側 (LearningPlansPanel) は描画のたびに
   *   新しい state を作って渡すので、参照で見分けると表示し直すたびに書きかけの入力が消えるためである。
   */
  const fillKey =
    state.status === "ready"
      ? JSON.stringify([merged.target, merged.subjects.map((s) => s.id), saved])
      : null;
  const [filledKey, setFilledKey] = useState<string | null>(null);
  if (fillKey !== null && fillKey !== filledKey) {
    setFilledKey(fillKey);
    setForm(formFromPlan(merged, saved));
    setDirty(false);
  }
  // 試験日は、手で入れた値があればそれ、無ければ生年月日から出した値を使う
  const examDate =
    saved?.exam_date && saved.exam_date.length > 0 ? saved.exam_date : derivedExamDate;
  const schedule = useMemo<SubjectSchedule[]>(
    () =>
      saved ? buildSchedule(merged, { ...saved, exam_date: examDate ?? "" }, todayLocal()) : [],
    [merged, saved, examDate],
  );
  const hasReview = Object.values(form.subjects).some((f) => f.enabled && f.mode === "review");
  const timeline = useMemo<ScheduleTimeline>(
    () => buildTimeline(schedule, examDate, gradeOf),
    [schedule, examDate, gradeOf],
  );
  // 低学年の区分を持つルートは6年分の列になるので、表を「低学年 / 高学年」で分けて出す。
  // 既定は今日が入る側で、今日がどちらにも入らなければ低学年 (これから始める子) にする
  const phases = useMemo(() => timelinePhases(timeline), [timeline]);
  const [phaseKey, setPhaseKey] = useState<TimelinePhase["key"] | null>(null);
  const today = todayLocal();
  const activePhase =
    phases.length === 0
      ? null
      : (phases.find((p) => p.key === phaseKey) ??
        phases.find((p) => {
          const c0 = timeline.columns[p.from]!;
          const c1 = timeline.columns[p.to]!;
          return today >= c0.startDate && today <= c1.endDate;
        }) ??
        phases[0]!);
  const shownTimeline =
    activePhase === null ? timeline : sliceTimeline(timeline, activePhase.from, activePhase.to);

  if (learnerPersonId === null) {
    return <p className="study-muted">学習者を選ぶと、その人の設定とスケジュールが出ます。</p>;
  }
  // 生年月日が届くまで本文を出さない。※なぜなら試験日と学年は生年月日から出すので、
  // 先に出すと空のスケジュールを表示してから中身が入り、その場で伸びるためである
  // (代表の指摘、2026-09-08)。
  if (state.status === "loading" || !birthLoaded) {
    return (
      <p role="status" className="study-muted study-panel-loading">
        設定を読み込んでいます...
      </p>
    );
  }
  if (state.status === "error") {
    return (
      <p role="alert" className="study-error">
        設定を読み込めませんでした。時間を置いて再読み込みしてください。
      </p>
    );
  }

  const update = (patch: Partial<PlanForm>) => {
    setForm((f) => ({ ...f, ...patch }));
    setDirty(true);
  };
  const updateSubject = (id: string, patch: Partial<SubjectForm>) => {
    setForm((f) => ({
      ...f,
      subjects: { ...f.subjects, [id]: { ...(f.subjects[id] ?? emptySubject()), ...patch } },
    }));
    setDirty(true);
  };

  return (
    <div className="study-exam-plan">
      {/* 設定はたたんでおき、スケジュールを先に見せる。※なぜなら設定は一度決めたら触る回数が少なく、
          開いたままだと毎回スケジュールまで長く送ることになるためである (代表の指示、2026-09-17) */}
      <details className="study-exam-settings">
        <summary>
          <h3 className="study-exam-heading">{learnerLabel}の設定</h3>
        </summary>
        <form
          className="study-exam-form"
          onSubmit={(e) => {
            e.preventDefault();
            // 保存できたら書きかけの印を落とす (設定の中身が変わらなかったときも保存ボタンを戻す)
            void save(toInput(merged, form)).then((ok) => {
              if (ok) setDirty(false);
            });
          }}
        >
          <div className="study-exam-row">
            <label>
              <span>{merged.scaleMarks.length > 0 ? "目標" : "目標の志望校"}</span>
              <select
                value={form.goalSchool}
                onChange={(e) => update({ goalSchool: e.target.value })}
              >
                <option value="">選ばない</option>
                {merged.schools.map((sc) => (
                  <option key={sc.name} value={sc.name}>
                    {sc.name}（{scaleMarkLabel(merged, sc.deviation)}）
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>{deadline}</span>
              <input
                type="date"
                value={form.examDate || derivedExamDate || ""}
                onChange={(e) => update({ examDate: e.target.value })}
              />
            </label>
            {derivedExamDate !== null && form.examDate === "" ? (
              <span className="study-exam-derived">
                生年月日から出しました（{gradeLabel(merged.examGrade ?? 0) ?? ""}の
                {formatMonthDay(merged.examMonthDay ?? "")}）
              </span>
            ) : derivedExamDate !== null && form.examDate !== derivedExamDate ? (
              <button
                type="button"
                className="study-link-btn"
                onClick={() => update({ examDate: "" })}
              >
                生年月日から出した日付（{derivedExamDate}）に戻す
              </button>
            ) : derivedExamDate === null && birthLoaded && merged.examGrade !== null ? (
              <span className="study-exam-derived">
                学習者の生年月日が登録されていれば、{deadline}は自動で入ります
              </span>
            ) : null}
            {hasReview ? (
              <label>
                <span>始める日（学び直しの科目）</span>
                <input
                  type="date"
                  value={form.startDate}
                  onChange={(e) => update({ startDate: e.target.value })}
                />
              </label>
            ) : null}
          </div>
          <p className="study-exam-help">
            初学者の科目は始める日を入れません。{deadline}
            から、参考書ルートに書かれた区分ごとの時期 (小4の春から基礎固め、など)
            をさかのぼって「いつから始めるか」を出します。時期の無いルートでは、今日から
            {deadline}までに割り付けます。学び直しの科目だけ、始める日を起点にします。
          </p>
          <div className="study-exam-subjects-scroll">
            <table className="study-exam-subjects">
              <thead>
                <tr>
                  <th scope="col">科目</th>
                  <th scope="col">受験科目</th>
                  <th scope="col">進め方</th>
                  <th scope="col">いまの{scaleNoun(merged)}</th>
                  <th scope="col">周回数</th>
                  <th scope="col">週の時間（分、任意）</th>
                </tr>
              </thead>
              <tbody>
                {merged.subjects.map((s) => {
                  const f = form.subjects[s.id] ?? emptySubject();
                  const toggleTrack = (tid: string) =>
                    updateSubject(s.id, {
                      excludedTracks: f.excludedTracks.includes(tid)
                        ? f.excludedTracks.filter((x) => x !== tid)
                        : [...f.excludedTracks, tid],
                    });
                  return (
                    <Fragment key={s.id}>
                      <tr>
                        <th scope="row">{s.label}</th>
                        <td>
                          <input
                            type="checkbox"
                            aria-label={`${s.label}を受験科目にする`}
                            checked={f.enabled}
                            onChange={(e) => updateSubject(s.id, { enabled: e.target.checked })}
                          />
                        </td>
                        <td>
                          <select
                            aria-label={`${s.label}の進め方`}
                            value={f.mode}
                            onChange={(e) =>
                              updateSubject(s.id, { mode: e.target.value as StudyExamMode })
                            }
                          >
                            {(Object.keys(STUDY_EXAM_MODE_LABELS) as StudyExamMode[]).map((m) => (
                              <option key={m} value={m}>
                                {STUDY_EXAM_MODE_LABELS[m]}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <input
                            type="number"
                            inputMode="decimal"
                            step="0.5"
                            min="0"
                            max="100"
                            aria-label={`${s.label}のいまの${scaleNoun(merged)}`}
                            value={f.currentDeviation}
                            disabled={f.mode !== "review"}
                            placeholder={f.mode === "review" ? "自己申告" : "最下段から"}
                            onChange={(e) =>
                              updateSubject(s.id, { currentDeviation: e.target.value })
                            }
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            inputMode="numeric"
                            min="1"
                            max="10"
                            aria-label={`${s.label}の周回数`}
                            value={f.rounds}
                            placeholder="空なら本ごと"
                            onChange={(e) => updateSubject(s.id, { rounds: e.target.value })}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            inputMode="numeric"
                            min="0"
                            step="30"
                            aria-label={`${s.label}の週の時間`}
                            placeholder={`空なら${deadline}から逆算`}
                            value={f.weeklyMinutes}
                            onChange={(e) => updateSubject(s.id, { weeklyMinutes: e.target.value })}
                          />
                        </td>
                      </tr>
                      {f.enabled && s.tracks.length > 0 ? (
                        <tr className="study-exam-tracks-row">
                          <td colSpan={6}>
                            <div
                              className="study-exam-tracks"
                              role="group"
                              aria-label={`${s.label}で使う区分`}
                            >
                              <span className="study-exam-tracks-label">使う区分</span>
                              {s.tracks.map((t) => {
                                const used = !f.excludedTracks.includes(t.id);
                                return (
                                  <button
                                    key={t.id}
                                    type="button"
                                    className="study-route-grid-track"
                                    aria-pressed={used}
                                    onClick={() => toggleTrack(t.id)}
                                  >
                                    {t.label}
                                  </button>
                                );
                              })}
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="study-exam-help">
            周回数は空なら本ごとの既定 (網羅系の問題集は3周、講義系の参考書と過去問は1周など)
            を使います。入れると、その科目の全部の本にその周回数を使います。既定の無い本は初学者
            {DEFAULT_ROUNDS.beginner}周・学び直し{DEFAULT_ROUNDS.review}周です。
            「使う区分」は受験科目にした科目に出ます。押して外した区分 (塾の教材・銀本など)
            の本はスケジュールに入れません。
          </p>
          <div className="study-exam-actions">
            <button type="submit" className="study-primary-btn" disabled={saving || !dirty}>
              {saving ? "保存しています..." : "保存する"}
            </button>
            {saveError ? (
              <span role="alert" className="study-error">
                {saveError}
              </span>
            ) : null}
          </div>
        </form>
      </details>

      <section className="study-exam-schedule" aria-label="学習スケジュール">
        <h3 className="study-exam-heading">スケジュール</h3>
        <p className="study-section-help">
          区分は「並行」と「順番」に分かれます。並行の区分 (計算・単語帳など)
          は期間の全体で続け、順番の区分 (基礎固め → 典型問題 → 実戦など)
          はこの順に進めます。どの列も同じ期間で終わるように、本の量に 比例して割り付けています。
        </p>
        {saved === null ? (
          <p className="study-muted">
            設定を保存すると、科目ごとの本の並びと週の割り付けが出ます。
          </p>
        ) : schedule.length === 0 ? (
          <p className="study-muted">
            受験科目にした科目がありません。表の「受験科目」に印を付けて保存してください。
          </p>
        ) : (
          <>
            {phases.length > 0 ? (
              <div className="study-exam-phases" role="group" aria-label="時系列の表の期間">
                {phases.map((p) => (
                  <button
                    key={p.key}
                    type="button"
                    className="study-route-grid-track"
                    aria-pressed={activePhase?.key === p.key}
                    onClick={() => setPhaseKey(p.key)}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            ) : null}
            <TimelineTable
              timeline={shownTimeline}
              examDate={examDate}
              deadline={deadline}
              shelf={shelf}
              {...(activity ? { activity } : {})}
            />
            {schedule.map((sub) => (
              <SubjectScheduleView
                key={sub.subjectId}
                sub={sub}
                examDate={examDate}
                deadline={deadline}
                gradeOf={gradeOf}
                shelf={shelf}
                {...(activity ? { activity } : {})}
              />
            ))}
          </>
        )}
      </section>
    </div>
  );
}

function SubjectScheduleView({
  sub,
  examDate,
  deadline,
  gradeOf,
  shelf,
  activity,
}: {
  sub: SubjectSchedule;
  examDate: string | null;
  /** 終わりの日の呼び名 (受験は「試験日」、教養・資格は「期限」)。 */
  deadline: string;
  shelf: ShelfBooks;
  gradeOf: ((date: string) => string | null) | null;
  activity?: ActivityByIsbn;
}) {
  // 今週 (月曜〜日曜) にこの科目の本へ実際に触れた時間である。予定との差や率は出さず、実績の数だけ並べる
  const today = todayLocal();
  const weekStart = weekStartOf(today);
  const weekSeconds = activity
    ? activitySeconds(
        activity,
        sub.lanes.flatMap((l) => l.books.map((b) => b.book.book.isbn)),
        weekStart,
        addDaysLocal(weekStart, 6),
      )
    : 0;
  const withGrade = (iso: string) => {
    const g = gradeOf !== null ? gradeOf(iso) : null;
    return g ? `${iso}（${g}）` : iso;
  };
  const emptyText =
    examDate === null
      ? `${deadline}を入れると期間が出ます`
      : `${deadline}を今日より後にし、量の分かる本があると期間が出ます`;
  const weeklyText =
    sub.weeklyMinutes === null
      ? emptyText
      : sub.startSource === "route"
        ? `平均で週${formatMinutes(sub.weeklyMinutes)}${
            sub.peakWeekly !== null && sub.peakWeekly > sub.weeklyMinutes
              ? `、最も重い時期は週${formatMinutes(sub.peakWeekly)}`
              : ""
          }`
        : sub.weeklySource === "derived"
          ? `${deadline}までに終えるには週${formatMinutes(sub.weeklyMinutes)}`
          : `週${formatMinutes(sub.weeklyMinutes)}`;
  const startText =
    sub.startSource === "route"
      ? `${sub.startLabel ? `${sub.startLabel}（${formatMonth(sub.startDate)}）` : formatMonth(sub.startDate)}から始める`
      : sub.startSource === "exam"
        ? `${deadline}から逆算すると${sub.startDate}に始める`
        : null;
  const settingWeekly =
    sub.setting.weekly_minutes && sub.setting.weekly_minutes > 0
      ? sub.setting.weekly_minutes
      : null;
  // 設定の週の時間で進めたとき、期限までに終えるのに必要な週の分を続けて出す文である。
  const requiredText =
    sub.weeklySource === "setting" && sub.requiredWeekly !== null && examDate !== null
      ? ` ・ ${deadline}（${examDate}）までに終えるには週${formatMinutes(sub.requiredWeekly)}が必要です`
      : null;
  return (
    <article className="study-exam-subject">
      <h4 className="study-exam-subject-title">
        {sub.subjectLabel}
        <span className="study-exam-subject-meta">
          {STUDY_EXAM_MODE_LABELS[sub.setting.mode]}
          {sub.setting.mode === "review" && sub.setting.current_deviation
            ? `（いま${sub.setting.current_deviation}）`
            : ""}
          {` ・ 合計${formatMinutes(sub.totalMinutes)}`}
          {sub.unknownVolume > 0 ? `（量が未登録の本${sub.unknownVolume}冊は含まない）` : ""}
        </span>
      </h4>
      {startText ? <p className="study-exam-start">{startText}</p> : null}
      <p className="study-exam-period">
        {sub.weeks !== null && sub.endDate ? (
          <>
            <b>
              {withGrade(sub.startDate)} 〜 {withGrade(sub.endDate)}
            </b>
            （{sub.weeks}週） ・ {weeklyText}
          </>
        ) : (
          weeklyText
        )}
        {sub.startSource === "route" && settingWeekly !== null && sub.peakWeekly !== null ? (
          <>
            {" "}
            ・ 設定の週{formatMinutes(settingWeekly)}
            {sub.peakWeekly > settingWeekly ? "では最も重い時期に足りません" : "で足ります"}
          </>
        ) : null}
        {requiredText}
        {weekSeconds >= 60 ? (
          <>
            {" "}
            ・{" "}
            <span className="study-exam-week-actual">
              今週の実績{formatMinutes(Math.floor(weekSeconds / 60))}
            </span>
          </>
        ) : null}
      </p>
      {sub.overExam && examDate ? (
        <p role="alert" className="study-error">
          この週の時間だと{deadline}（{examDate}
          ）を越えます。週の時間を増やすか、周回数か本を減らしてください。
        </p>
      ) : null}
      {sub.lanes.length === 0 ? (
        <p className="study-muted">この設定で使う本がありません。</p>
      ) : (
        <div className="study-exam-lanes">
          {sub.lanes.map((lane) => (
            <LaneView key={lane.id} lane={lane} shelf={shelf} />
          ))}
        </div>
      )}
    </article>
  );
}

/** 列1本を描く。本の並びと日付、周回が2回以上なら周回ごとの期間を出す。 */
function LaneView({ lane, shelf }: { lane: Lane; shelf: ShelfBooks }) {
  return (
    <section className="study-exam-lane" aria-label={lane.label}>
      <h5 className="study-exam-lane-title">
        <span className={`study-exam-lane-flow study-exam-lane-flow--${lane.flow}`}>
          {lane.flow === "in_turn" ? "順番" : "並行"}
        </span>
        {lane.label}
        <span className="study-exam-subject-meta">
          {formatMinutes(lane.minutes)}
          {lane.unknownVolume > 0 ? `（量が未登録の本${lane.unknownVolume}冊は含まない）` : ""}
        </span>
      </h5>
      <ol className="study-exam-books">
        {lane.books.map((b, i) => (
          <li key={`${b.book.key}-${i}`} className="study-exam-book">
            <BookCover b={b} shelf={shelf} />
            <div className="study-exam-book-main">
              <span className="study-exam-book-title">{b.book.book.title}</span>
              <span className="study-exam-book-meta">
                <span className="study-route-grid-book-track">{b.book.trackLabel}</span>
                {b.reach !== null ? <span>届く目安 {b.reach}</span> : <span>目安なし</span>}
                {b.minutes !== null ? (
                  <span>
                    {b.book.book.volume}
                    {b.book.book.volume_unit} × {b.rounds}周 ≈ {formatMinutes(b.minutes)}
                    {b.rounds > 1 && !b.book.book.rounds ? "（設定の周回数）" : ""}
                  </span>
                ) : (
                  <span className="study-exam-book-unknown">量が未登録</span>
                )}
              </span>
            </div>
            <div className="study-exam-book-when">
              {b.startDate && b.endDate ? (
                <>
                  {b.startDate} 〜 {b.endDate}
                  <span className="study-exam-book-weeks">
                    {b.startWeek === b.endWeek
                      ? `第${(b.startWeek ?? 0) + 1}週`
                      : `第${(b.startWeek ?? 0) + 1}〜${(b.endWeek ?? 0) + 1}週`}
                  </span>
                  {b.passes.length > 1 ? (
                    <span className="study-exam-book-passes">
                      {b.passes.map((p) => (
                        <span key={p.round}>
                          {p.round}周目 {p.startDate.slice(5).replace("-", "/")}〜
                          {p.endDate.slice(5).replace("-", "/")}
                        </span>
                      ))}
                    </span>
                  ) : null}
                </>
              ) : b.share !== null ? (
                <span className="study-muted">列の{Math.round(b.share * 100)}%</span>
              ) : (
                <span className="study-muted">日付なし</span>
              )}
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}

/** 小さい表紙 (タップで拡大)。本だなにある本には、表紙が左下に印を出す。 */
function BookCover({ b, shelf }: { b: ScheduledBook; shelf: ShelfBooks }) {
  return (
    <span className="study-cover-sm">
      <MaterialCover
        isbn={b.book.book.isbn}
        url={b.book.book.url}
        title={b.book.book.title}
        enlargeable
        onShelf={shelf.has(b.book.book.isbn)}
      />
    </span>
  );
}

/**
 * 全科目を1枚の時系列の表に描く。
 *
 * @what: 見出しの行が週 (26週まで) か月、行が科目 × 列 (lane)。本は startWeek〜endWeek に当たる
 *   列を colspan でまたぎ、表紙と書名を出す。試験日の列に印を付け、量が未登録で日付の決まらない本は
 *   冊数だけ出す (書名は下の科目ごとの一覧にある)。
 * @why: 「いつ・どの科目で・何をやっているか」を1枚で見たい (代表の要望、2026-09-05)。
 */
function TimelineTable({
  timeline,
  examDate,
  deadline,
  shelf,
  activity,
}: {
  timeline: ScheduleTimeline;
  examDate: string | null;
  /** 終わりの日の呼び名 (受験は「試験日」、教養・資格は「期限」)。 */
  deadline: string;
  shelf: ShelfBooks;
  /** 渡すと、本の下にその区間の列ごとの「触れた週」の印を置く。 */
  activity?: ActivityByIsbn;
}) {
  if (timeline.columns.length === 0) {
    return (
      <p className="study-muted">
        期間が決まらないので時系列の表は出せません。週の時間か{deadline}を入れてください。
      </p>
    );
  }
  const n = timeline.columns.length;
  return (
    <div className="study-exam-timeline-wrap">
      <table
        className={`study-exam-timeline study-exam-timeline--${timeline.granularity}`}
        aria-label="時系列の表"
      >
        <thead>
          <tr>
            <th scope="col" colSpan={2} className="study-exam-timeline-rowhead">
              科目と列
            </th>
            {timeline.columns.map((c, i) => (
              <th
                key={c.key}
                scope="col"
                className={`study-exam-timeline-colhead${
                  timeline.examColumn === i ? " study-exam-timeline-colhead--exam" : ""
                }`}
              >
                {c.label}
                {c.grade ? <span className="study-exam-timeline-grade">{c.grade}</span> : null}
                {timeline.examColumn === i && examDate ? (
                  <span className="study-exam-timeline-exam">
                    {deadline === "期限" ? "期限" : "試験"} {examDate.slice(5).replace("-", "/")}
                  </span>
                ) : null}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {timeline.rows.map((row) => {
            const cells: ReactNode[] = [];
            let cursor = 0;
            for (const seg of row.segments) {
              if (seg.from > cursor) {
                cells.push(
                  <td
                    key={`gap-${cursor}`}
                    colSpan={seg.from - cursor}
                    className="study-exam-timeline-gap"
                  />,
                );
              }
              cells.push(
                <td
                  key={`seg-${seg.from}`}
                  colSpan={seg.to - seg.from + 1}
                  className={`study-exam-timeline-seg study-exam-timeline-seg--${row.lane.flow}`}
                >
                  <ul className="study-exam-timeline-books">
                    {seg.items.map((it, i) => (
                      <li
                        key={`${it.book.book.key}-${it.round ?? 0}-${i}`}
                        className="study-exam-timeline-book"
                      >
                        <BookCover b={it.book} shelf={shelf} />
                        <span className="study-exam-timeline-book-body">
                          <span className="study-exam-timeline-book-title">
                            {it.book.book.book.title}
                            {it.round !== null ? (
                              <span className="study-exam-timeline-round">{it.round}周目</span>
                            ) : null}
                          </span>
                          <span className="study-exam-timeline-book-when">
                            {it.startDate.slice(5).replace("-", "/")}〜
                            {it.endDate.slice(5).replace("-", "/")}
                          </span>
                        </span>
                      </li>
                    ))}
                  </ul>
                  {activity ? (
                    <TouchMarks
                      activity={activity}
                      isbns={seg.items.map((it) => it.book.book.book.isbn)}
                      columns={timeline.columns.slice(seg.from, seg.to + 1)}
                    />
                  ) : null}
                </td>,
              );
              cursor = seg.to + 1;
            }
            if (cursor < n) {
              cells.push(
                <td key="gap-tail" colSpan={n - cursor} className="study-exam-timeline-gap" />,
              );
            }
            return (
              <tr key={`${row.subjectId}-${row.lane.id}`}>
                {row.firstOfSubject ? (
                  <th
                    scope="row"
                    rowSpan={row.laneCount}
                    className="study-exam-timeline-rowhead study-exam-timeline-subject"
                  >
                    {row.subjectLabel}
                  </th>
                ) : null}
                <th scope="row" className="study-exam-timeline-lanehead">
                  <span className={`study-exam-lane-flow study-exam-lane-flow--${row.lane.flow}`}>
                    {row.lane.flow === "in_turn" ? "順番" : "並行"}
                  </span>
                  <span className="study-exam-timeline-lane-label">{row.lane.label}</span>
                  {row.undated.length > 0 ? (
                    <span
                      className="study-exam-timeline-undated"
                      title={row.undated.map((b) => b.book.book.title).join("、")}
                    >
                      量が未登録の本{row.undated.length}冊は表に出ません
                    </span>
                  ) : null}
                </th>
                {cells}
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="study-exam-timeline-legend">
        白いマスは順番に進める列、青いマスは並行して進める列です。周回が2回以上の本は「2周目」のように周回ごとに置きます。
      </p>
    </div>
  );
}

/**
 * 区間の列ごとに、その本へ触れた週 (月) があるかを小さな印で置く。
 *
 * @what: 触れた列だけ濃くする。触れていない列は薄いままで、警告や色は付けない (遅れを言わない)。
 *   1つでも触れた列が無ければ何も出さない (これから始める本の下に空の目盛りを並べない)。
 */
function TouchMarks({
  activity,
  isbns,
  columns,
}: {
  activity: ActivityByIsbn;
  isbns: readonly (string | undefined)[];
  columns: readonly { key: string; startDate: string; endDate: string; label: string }[];
}) {
  const on = columns.map((c) => touchedWithin(activity, isbns, c.startDate, c.endDate));
  if (!on.some(Boolean)) return null;
  return (
    <span
      className="study-exam-timeline-touch"
      role="img"
      aria-label={`触れた時期 ${columns
        .filter((_, i) => on[i])
        .map((c) => c.label)
        .join("、")}`}
      style={{ gridTemplateColumns: `repeat(${columns.length}, 1fr)` }}
    >
      {columns.map((c, i) => (
        <span
          key={c.key}
          className={`study-exam-timeline-touch-cell${on[i] ? " study-exam-timeline-touch-cell--on" : ""}`}
        />
      ))}
    </span>
  );
}

/** "2026-04-01" を「2026年4月」で出す。 */
function formatMonth(iso: string): string {
  return `${iso.slice(0, 4)}年${Number(iso.slice(5, 7))}月`;
}
